// import React, { createContext, useContext, useState } from 'react';

// type ToastType = 'success' | 'error' | 'warning' | 'info';

// interface Toast {
//   id: number;
//   message: string;
//   type: ToastType;
// }

// interface ToastContextType {
//   showToast: (toast: { message: string; type: ToastType }) => void;
// }

// const ToastContext = createContext<ToastContextType | undefined>(undefined);

// export function ToastProvider({ children }: { children: React.ReactNode }) {
//   const [toasts, setToasts] = useState<Toast[]>([]);
//   const MAX_TOASTS = 3;

//   const showToast = ({ message, type }: { message: string; type: ToastType }) => {
//     const id = Date.now();

//     setToasts((prev) => {
//       if (prev.some((t) => t.message === message)) {
//         return prev;
//       }
//       return [...prev, { id, message, type }].slice(-MAX_TOASTS);
//     });

//     setTimeout(() => {
//       setToasts((prev) => prev.filter((t) => t.id !== id));
//     }, 3000);
//   };

//   return (
//     <ToastContext.Provider value={{ showToast }}>
//       {children}
//       {/* Toast container */}
//       <div className="fixed top-4 right-4 z-50 space-y-2" role="status" aria-live="polite">
//         {toasts.map((toast) => (
//           <div
//             key={toast.id}
//             className={`p-4 rounded shadow-lg text-white transition-opacity duration-300 ${toast.type === 'success'
//               ? 'bg-green-500'
//               : toast.type === 'error'
//                 ? 'bg-red-500'
//                 : toast.type === 'warning'
//                   ? 'bg-yellow-500'
//                   : 'bg-blue-500'
//               }`}
//           >
//             {toast.message}
//           </div>
//         ))}
//       </div>
//     </ToastContext.Provider>
//   );
// }

// export function useToast() {
//   const context = useContext(ToastContext);
//   if (!context) {
//     throw new Error('useToast must be used within a ToastProvider');
//   }
//   return context;
// }





import React, { createContext, useContext, useState, useEffect } from "react";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";

type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  isVisible?: boolean;
}

interface ToastContextType {
  showToast: (toast: { message: any; type: ToastType; duration?: number }) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const MAX_TOASTS = 4;

  const showToast = ({
    message,
    type,
    duration = 4000
  }: {
    message: string;
    type: ToastType;
    duration?: number
  }) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    setToasts((prev) => {
      // Prevent duplicate messages
      if (prev.some((t) => t.message === message && t.type === type)) {
        return prev;
      }

      const newToast: Toast = { id, message, type, duration, isVisible: false };
      const updatedToasts = [...prev, newToast].slice(-MAX_TOASTS);

      // Trigger animation after state update
      setTimeout(() => {
        setToasts(current =>
          current.map(toast =>
            toast.id === id ? { ...toast, isVisible: true } : toast
          )
        );
      }, 10);

      return updatedToasts;
    });

    // Auto-dismiss
    setTimeout(() => {
      dismissToast(id);
    }, duration);
  };

  const dismissToast = (id: string) => {
    setToasts(prev =>
      prev.map(toast =>
        toast.id === id ? { ...toast, isVisible: false } : toast
      )
    );

    // Remove from array after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  };

  const icons: Record<ToastType, JSX.Element> = {
    success: <CheckCircle className="w-5 h-5" />,
    error: <XCircle className="w-5 h-5" />,
    warning: <AlertTriangle className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />,
  };

  const styles: Record<ToastType, string> = {
    success: "bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200 text-emerald-800 shadow-emerald-100",
    error: "bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-800 shadow-red-100",
    warning: "bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 text-amber-800 shadow-amber-100",
    info: "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-800 shadow-blue-100",
  };

  const iconStyles: Record<ToastType, string> = {
    success: "text-emerald-600",
    error: "text-red-600",
    warning: "text-amber-600",
    info: "text-blue-600",
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-6 right-6 z-[9999] space-y-3 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              flex items-start gap-3 px-5 py-4 rounded-2xl border backdrop-blur-sm
              shadow-lg transform transition-all duration-300 ease-out pointer-events-auto
              ${styles[toast.type]}
              ${toast.isVisible
                ? 'translate-x-0 opacity-100 scale-100'
                : 'translate-x-full opacity-0 scale-95'
              }
              hover:shadow-xl hover:scale-[1.02]
              max-w-sm min-w-[280px]
            `}
          >
            <div className={`flex-shrink-0 mt-0.5 ${iconStyles[toast.type]}`}>
              {icons[toast.type]}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-relaxed break-words">
                {toast.message}
              </p>
            </div>

            <button
              onClick={() => dismissToast(toast.id)}
              className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-black/5 
                         transition-colors duration-200 group"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}